import React, { useEffect, useState } from "react";
import { Card, Alert, Badge, ListGroup, Spinner } from "react-bootstrap";
import { fetchLivreursPositions } from "./DashboardService";

function LivreursAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Seuils de détection des problèmes
  const THRESHOLDS = {
    GPS_OFFLINE_MINUTES: 5,        // Pas de mise à jour GPS depuis 5 minutes
    GPS_POOR_ACCURACY: 50,         // Précision GPS > 50 mètres
    INACTIVE_MINUTES: 15,          // Livreur disponible mais inactif depuis 15 minutes
    SHIFT_DURATION_HOURS: 10,      // Shift de plus de 10 heures (fatigue potentielle)
  };

  // Types d'alertes avec leurs configurations
  const ALERT_TYPES = {
    GPS_OFFLINE: {
      severity: "danger",
      icon: "📡",
      title: "GPS hors ligne",
      priority: 1,
    },
    GPS_POOR_ACCURACY: {
      severity: "warning",
      icon: "📍",
      title: "Précision GPS faible",
      priority: 3,
    },
    DRIVER_INACTIVE: {
      severity: "warning",
      icon: "⏱️",
      title: "Livreur inactif",
      priority: 2,
    },
    LONG_SHIFT: {
      severity: "info",
      icon: "🕐",
      title: "Shift prolongé",
      priority: 4,
    },
    STUCK_DELIVERY: {
      severity: "danger",
      icon: "🚨",
      title: "Livraison bloquée",
      priority: 1,
    },
  };

  // Fonction pour analyser les livreurs et détecter les problèmes
  const analyzeDrivers = (livreurs) => {
    const detectedAlerts = [];

    livreurs.forEach((livreur) => {
      const minutesSinceUpdate = livreur.minutes_since_last_update || 0;

      // 1. Alerte GPS hors ligne
      if (livreur.is_online && minutesSinceUpdate >= THRESHOLDS.GPS_OFFLINE_MINUTES) {
        detectedAlerts.push({
          type: "GPS_OFFLINE",
          livreur: livreur,
          message: `Aucune mise à jour GPS depuis ${minutesSinceUpdate} minutes`,
          details: `Dernière position: ${livreur.last_update ? new Date(livreur.last_update).toLocaleString('fr-FR') : 'Inconnue'}`,
        });
      }

      // 2. Alerte précision GPS faible
      if (livreur.accuracy && livreur.accuracy > THRESHOLDS.GPS_POOR_ACCURACY) {
        detectedAlerts.push({
          type: "GPS_POOR_ACCURACY",
          livreur: livreur,
          message: `Précision GPS faible: ${Math.round(livreur.accuracy)}m`,
          details: "La position peut être imprécise",
        });
      }

      // 3. Alerte livreur disponible mais inactif
      if (
        livreur.status === "available" &&
        livreur.is_online &&
        minutesSinceUpdate < THRESHOLDS.GPS_OFFLINE_MINUTES &&
        minutesSinceUpdate >= THRESHOLDS.INACTIVE_MINUTES
      ) {
        detectedAlerts.push({
          type: "DRIVER_INACTIVE",
          livreur: livreur,
          message: `Disponible mais aucune activité depuis ${minutesSinceUpdate} minutes`,
          details: "Vérifier si le livreur a besoin d'une nouvelle livraison",
        });
      }

      // 4. Alerte shift prolongé (fatigue potentielle)
      if (livreur.shift_started_at) {
        const shiftStartTime = new Date(livreur.shift_started_at);
        const now = new Date();
        const shiftHours = (now - shiftStartTime) / (1000 * 60 * 60);

        if (shiftHours >= THRESHOLDS.SHIFT_DURATION_HOURS) {
          detectedAlerts.push({
            type: "LONG_SHIFT",
            livreur: livreur,
            message: `En service depuis ${Math.round(shiftHours)} heures`,
            details: `Début du service: ${shiftStartTime.toLocaleTimeString('fr-FR')}`,
          });
        }
      }

      // 5. Alerte livraison bloquée (en livraison depuis trop longtemps)
      if (
        livreur.status === "delivering" &&
        livreur.current_commande_id &&
        minutesSinceUpdate >= THRESHOLDS.INACTIVE_MINUTES
      ) {
        detectedAlerts.push({
          type: "STUCK_DELIVERY",
          livreur: livreur,
          message: `Livraison N°${livreur.current_commande_id} en cours depuis ${minutesSinceUpdate}+ minutes`,
          details: "Vérifier si le livreur rencontre un problème",
        });
      }
    });

    // Trier les alertes par priorité
    return detectedAlerts.sort((a, b) => {
      const priorityA = ALERT_TYPES[a.type]?.priority || 99;
      const priorityB = ALERT_TYPES[b.type]?.priority || 99;
      return priorityA - priorityB;
    });
  };

  // Fonction pour récupérer les positions et analyser
  const fetchAndAnalyze = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchLivreursPositions();

      if (response.success) {
        const livreurs = response.livreurs || [];
        const detectedAlerts = analyzeDrivers(livreurs);
        setAlerts(detectedAlerts);
      }
    } catch (err) {
      console.error("Erreur lors de l'analyse des livreurs:", err);
      setError("Impossible de charger les alertes");
    } finally {
      setLoading(false);
    }
  };

  // Chargement initial et rafraîchissement toutes les 30 secondes
  useEffect(() => {
    fetchAndAnalyze();
    const interval = setInterval(fetchAndAnalyze, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fonction pour obtenir la configuration de l'alerte
  const getAlertConfig = (type) => {
    return ALERT_TYPES[type] || { severity: "secondary", icon: "ℹ️", title: "Alerte" };
  };

  if (loading && alerts.length === 0) {
    return (
      <Card className="border-0 mb-4">
        <Card.Body className="text-center">
          <Spinner animation="border" role="status" size="sm">
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
          <span className="ms-2">Analyse des livreurs...</span>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border-0 mb-4">
      <Card.Body>
        <Card.Title className="d-flex justify-content-between align-items-center">
          <span>🚨 Alertes et Monitoring</span>
          <Badge bg={alerts.length > 0 ? "danger" : "success"}>
            {alerts.length} alerte{alerts.length !== 1 ? "s" : ""}
          </Badge>
        </Card.Title>

        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}

        {alerts.length === 0 && !loading && (
          <Alert variant="success" className="mt-3 mb-0">
            <strong>✅ Aucun problème détecté</strong>
            <p className="mb-0 mt-2 small">
              Tous les livreurs fonctionnent normalement.
            </p>
          </Alert>
        )}

        {alerts.length > 0 && (
          <ListGroup className="mt-3">
            {alerts.map((alert, index) => {
              const config = getAlertConfig(alert.type);
              return (
                <ListGroup.Item
                  key={index}
                  variant={config.severity}
                  className="d-flex justify-content-between align-items-start"
                >
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-2">
                      <span className="me-2" style={{ fontSize: "1.2rem" }}>
                        {config.icon}
                      </span>
                      <div>
                        <strong>{alert.livreur.name}</strong>
                        <Badge bg="secondary" className="ms-2 small">
                          {config.title}
                        </Badge>
                      </div>
                    </div>
                    <div className="mb-1">
                      <strong>{alert.message}</strong>
                    </div>
                    <small className="text-muted d-block">{alert.details}</small>
                    <small className="text-muted d-block">
                      📞 {alert.livreur.phone}
                      {alert.livreur.latitude && alert.livreur.longitude && (
                        <span className="ms-3">
                          📍{" "}
                          <a
                            href={`https://www.google.com/maps?q=${alert.livreur.latitude},${alert.livreur.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-decoration-none"
                          >
                            Voir position
                          </a>
                        </span>
                      )}
                    </small>
                  </div>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        )}

        <div className="mt-3 text-end">
          <small className="text-muted">
            Mise à jour automatique toutes les 30 secondes • Dernière analyse:{" "}
            {new Date().toLocaleTimeString('fr-FR')}
          </small>
        </div>

        {/* Légende des seuils */}
        <Card className="mt-3 bg-light border-0">
          <Card.Body className="py-2">
            <small className="text-muted d-block mb-1">
              <strong>Seuils de détection:</strong>
            </small>
            <div className="row small text-muted">
              <div className="col-md-6">
                • GPS hors ligne: {THRESHOLDS.GPS_OFFLINE_MINUTES} min
              </div>
              <div className="col-md-6">
                • Précision GPS faible: &gt; {THRESHOLDS.GPS_POOR_ACCURACY}m
              </div>
              <div className="col-md-6">
                • Livreur inactif: {THRESHOLDS.INACTIVE_MINUTES} min
              </div>
              <div className="col-md-6">
                • Shift prolongé: {THRESHOLDS.SHIFT_DURATION_HOURS}h
              </div>
            </div>
          </Card.Body>
        </Card>
      </Card.Body>
    </Card>
  );
}

export default LivreursAlerts;
